/**
 * Small motion pieces shared across the app. Everything here is
 * presentation only: no data, no behavior, and all of it yields to
 * prefers-reduced-motion through the rules in dashboard.css.
 *
 *   <Glide/>          an indicator that slides between the active items of
 *                     its parent (sidebar highlight, tab underline, pill).
 *   <Pop on/>         the icon pop + gold ring when a like/save turns on.
 *   <RollingCount/>   a number that rolls to its new value.
 *   <DrawCheck/>      a check that draws itself.
 *   <CompleteCheck/>  a DrawCheck that only draws when its step turns
 *                     complete while you watch, not on every page load.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// Position of `el` inside `host`, walked through offsetParents so a
// transform on the way (a page rising in, a nav section sliding) does not
// skew it. Null when `host` is not one of el's offsetParents, which is
// also what jsdom gives, so tests just see no indicator.
function offsetWithin(el, host) {
  let x = 0, y = 0, n = el;
  while (n && n !== host) {
    x += n.offsetLeft; y += n.offsetTop;
    const p = n.offsetParent;
    if (!p || !host.contains(p)) return null;
    if (p !== host) { x += p.clientLeft; y += p.clientTop; }
    n = p;
  }
  return n === host ? { x, y } : null;
}

/**
 * One indicator for a row (or column) of items. Drop it in as a child of the
 * container, give the container position:relative, and mark the current
 * item (data-active="true" by default, or pass a selector). It measures the
 * item and slides there when the item changes, instead of each item drawing
 * its own highlight and the highlight jumping.
 *
 * While it is showing it sets data-glide="<variant>" on the container, which
 * is what dashboard.css keys on to hide the item's own highlight. If it can't
 * measure (no layout yet, item folded away) it takes the attribute off again
 * and the item's own highlight is back, so nothing ever ends up unmarked.
 */
export function Glide({ variant = "pill", selector = '[data-active="true"]', inset = 0, style }) {
  const ref = useRef(null);
  const [box, setBox] = useState(null);
  const [live, setLive] = useState(false);

  useLayoutEffect(() => {
    const me = ref.current;
    const host = me && me.parentElement;
    if (!host) return undefined;
    let raf = 0, liveRaf = 0, dead = false;
    let ro = null;

    const hide = () => {
      setBox(null); setLive(false);
      if (liveRaf) { cancelAnimationFrame(liveRaf); liveRaf = 0; }
      if (host.hasAttribute("data-glide")) host.removeAttribute("data-glide");
    };
    const measure = () => {
      raf = 0;
      if (dead) return;
      if (ro) Array.from(host.children).forEach(c => { if (c !== me) ro.observe(c); });
      const t = host.querySelector(selector);
      const o = t && t.offsetWidth > 0 && !t.closest('[aria-hidden="true"]') ? offsetWithin(t, host) : null;
      if (!o) { hide(); return; }
      const next = { x: o.x + inset, y: o.y, w: Math.max(0, t.offsetWidth - inset * 2), h: t.offsetHeight };
      setBox(b => (b && b.x === next.x && b.y === next.y && b.w === next.w && b.h === next.h) ? b : next);
      if (host.getAttribute("data-glide") !== variant) host.setAttribute("data-glide", variant);
      // The first placement lands without a slide; only moves after it animate.
      if (!liveRaf) liveRaf = requestAnimationFrame(() => {
        liveRaf = requestAnimationFrame(() => { if (!dead) setLive(true); });
      });
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };

    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(schedule);
      ro.observe(host);
    }
    measure();
    const mo = new MutationObserver(schedule);
    mo.observe(host, { subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ["class", "data-active", "aria-selected", "aria-hidden", "data-open"] });
    window.addEventListener("resize", schedule);
    // Web fonts arrive after the first paint and change every label's width.
    try { document.fonts?.ready?.then(schedule).catch(() => {}); } catch {}

    return () => {
      dead = true;
      mo.disconnect();
      if (ro) ro.disconnect();
      window.removeEventListener("resize", schedule);
      if (raf) cancelAnimationFrame(raf);
      if (liveRaf) cancelAnimationFrame(liveRaf);
      host.removeAttribute("data-glide");
    };
  }, [selector, variant, inset]);

  const pos = box ? {
    width: box.w,
    height: variant === "underline" ? undefined : box.h,
    transform: `translate(${box.x}px, ${variant === "underline" ? box.y + box.h - 2 : box.y}px)`,
  } : null;
  return (
    <span ref={ref} aria-hidden="true"
      className={`bam-glide bam-glide-${variant}${box ? " is-on" : ""}${live ? " is-live" : ""}`}
      style={{ ...style, ...pos }}/>
  );
}

/**
 * Wraps a like / save / favorite icon. When `on` flips to true right after
 * the coach pressed the control it sits in, the icon pops and a gold ring
 * goes out from it. Turning on for any other reason (the saved list
 * arriving from the server, a second device) stays still, so a grid of
 * bookmarked drills doesn't go off like popcorn on load.
 */
export function Pop({ on, children, style }) {
  const ref = useRef(null);
  const armedAt = useRef(0);
  const prev = useRef(on);
  const [n, setN] = useState(0);

  useEffect(() => {
    const el = ref.current;
    const host = el && (el.closest('button,[role="button"],.btn,.lbtn,.post-action') || el.parentElement);
    if (!host) return undefined;
    const arm = () => { armedAt.current = Date.now(); };
    host.addEventListener("pointerdown", arm);
    host.addEventListener("click", arm, true);
    host.addEventListener("keydown", arm);
    return () => {
      host.removeEventListener("pointerdown", arm);
      host.removeEventListener("click", arm, true);
      host.removeEventListener("keydown", arm);
    };
  }, []);

  useEffect(() => {
    if (on && !prev.current && Date.now() - armedAt.current < 1500) setN(x => x + 1);
    prev.current = on;
  }, [on]);

  // A new key restarts the animation on every fresh press.
  return (
    <span ref={ref} key={n} className={`bam-pop${n ? " is-on" : ""}`} style={style}>
      {children}
    </span>
  );
}

/**
 * A count that rolls: the old number slides out and the new one slides in,
 * up when it grew and down when it shrank. `format` turns the raw number
 * into what's shown (1.2k and so on).
 */
export function RollingCount({ value, format = (v) => v, style }) {
  const last = useRef(value);
  const [out, setOut] = useState(null);

  useEffect(() => {
    if (last.current === value) return undefined;
    const dir = Number(value) >= Number(last.current) ? "up" : "down";
    setOut({ v: last.current, dir, k: Date.now() });
    last.current = value;
    const t = setTimeout(() => setOut(null), 260);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <span className="bam-roll" style={style}>
      {out && <span key={`o${out.k}`} className={`bam-roll-out ${out.dir}`} aria-hidden="true">{format(out.v)}</span>}
      <span key={`i${out ? out.k : 0}`} className={out ? `bam-roll-in ${out.dir}` : undefined}>{format(value)}</span>
    </span>
  );
}

/** A check mark (optionally in a circle) that draws itself when `draw`. */
export function DrawCheck({ size = 16, color = "currentColor", strokeWidth = 2.5, circle = false, draw = true, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      className={draw ? "bam-check-draw" : undefined} style={{ flexShrink: 0, ...style }}>
      {circle && <path d="M12 2a10 10 0 1 1 0 20a10 10 0 1 1 0-20" pathLength="1"/>}
      <path d={circle ? "m8 12.5 2.8 2.8L16.5 9" : "M20 6 9 17l-5-5"} pathLength="1"/>
    </svg>
  );
}

/**
 * True once `v` has gone false -> true while this component was mounted.
 * `key` resets it, so stepping to a different lesson that was already done
 * doesn't count as completing it.
 */
export function useBecameTrue(v, key) {
  const s = useRef({ key, wasFalse: !v });
  if (s.current.key !== key) s.current = { key, wasFalse: !v };
  else if (!v) s.current.wasFalse = true;
  return !!v && s.current.wasFalse;
}

/**
 * Renders nothing until `on`, then a check. The check draws itself only
 * when `on` turned true while it was on screen. Render it unconditionally
 * so it can see the change happen.
 */
export function CompleteCheck({ on, resetKey, ...props }) {
  const fresh = useBecameTrue(on, resetKey);
  return on ? <DrawCheck draw={fresh} {...props}/> : null;
}
